import {
  Container,
  Box,
  Header,
  Button,
  Grid,
  SpaceBetween,
  Input,
  ExpandableSection,
  FormField,
  Select,
  SelectProps,
} from '@cloudscape-design/components';

import { ViewLayout } from '../../components/ViewLayout';
import { useEffect, useEffectEvent, useMemo, useState } from 'react';
import {
  CreateReportResponse,
  GOOGLE_PIPELINE_TASKS,
  PlaceReport,
  ReportContactRequest,
  Task,
} from '../../common/types';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createReports, getReport } from '../../common/api/reports';
import { LoadingOrError } from '../../components/Loading';
import { LocalTopNavigation } from '../../common/navigation';
import AutoCompleteSearch from '../../components/AutoCompleteSearch';
import MultiAutoCompleteSearch from '../../components/MultiAutoCompleteSearch';
import { Suggestion } from '../../common/useMaps';
import { BUSINESS_TYPES } from '../../common/types';
import { getBusinessType } from '../../common/businessTypes';
import ReportCardHomeHeader from '../../components/ReportCardHomeHeader';
import {
  ScorecardLoadingView,
  ScorecardLoadingReport,
} from '../../components/ScorecardLoadingView';

export default function View() {
  const [selectedItems, setSelectedItems] = useState<Suggestion[]>([]);
  const [reports, setReports] = useState<CreateReportResponse[]>([]);

  const createMutation = useMutation({
    mutationFn: createReports,
    onSuccess: (data) => {
      setSelectedItems([]);
      setReports(data.filter((d): d is CreateReportResponse => d !== undefined));
    },
  });

  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const [businessType, setBusinessType] = useState<SelectProps.Option | null>(null);

  const validPhoneNumber = useMemo(() => {
    if (!phoneNumber.trim()) {
      return false;
    }
    return phoneNumber.trim().length >= 10 && phoneNumber.trim().length <= 15;
  }, [phoneNumber]);

  const validEmail = useMemo(() => {
    if (!email.trim()) {
      return false;
    }
    return email.trim().includes('@');
  }, [email]);

  const hasContactInfo = useMemo(() => {
    return validEmail || validPhoneNumber;
  }, [validEmail, validPhoneNumber]);

  const invalidContact = useMemo(() => {
    // if they have a first name but no contact info, invalid
    if (firstName.trim() && !hasContactInfo) {
      return true;
    }
    // if they have no first name but have contact info, invalid
    if (!firstName.trim() && hasContactInfo) {
      return true;
    }
    return false;
  }, [firstName, lastName, email, phoneNumber]);

  const anyContact = useMemo(() => {
    return !!(
      firstName.trim() ||
      lastName.trim() ||
      email.trim() ||
      phoneNumber.trim() ||
      title.trim()
    );
  }, [firstName, lastName, email, phoneNumber, title]);

  const handleCreate = () => {
    if (createMutation.isPending) {
      return;
    }
    if (selectedItems.length === 0) {
      return;
    }

    let contact: ReportContactRequest | undefined;
    if (anyContact) {
      if (invalidContact) {
        setShowErrors(true);
        return;
      }
      contact = {
        first_name: firstName.trim(),
        last_name: lastName.trim() || undefined,
        email: email.trim() || undefined,
        phone_number: phoneNumber.trim() || undefined,
        title: title.trim() || undefined,
      };
    }
    setReports([]);
    createMutation.mutate({
      place_ids: selectedItems.map((item) => item.placeId),
      contact,
      business_type:
        businessType?.value === 'Other' ? undefined : (businessType?.value ?? undefined),
    });
  };

  useEffect(() => {
    if (selectedItems.length > 0) {
      // try to find the best business type
      const firstItem = selectedItems[0];
      const googleTags = firstItem.tags;
      const businessType = getBusinessType(googleTags);
      if (businessType) {
        setBusinessType({ label: businessType, value: businessType });
      } else {
        setBusinessType({ label: 'Other', value: 'Other' });
      }
    }
  }, [selectedItems]);

  return (
    <>
      <LocalTopNavigation />
      <ViewLayout
        header={<ReportCardHomeHeader />}
        contentType="dashboard"
        navigationHide
        headerVariant="high-contrast"
      >
        <Grid
          gridDefinition={[
            {
              colspan: {
                default: 12,
              },
            },
            {
              colspan: {
                default: 12,
              },
            },
          ]}
        >
          <Container
            header={
              <Header
                variant="h1"
                description="Enter a business name to create a Reputation Scorecard from Google Reviews"
                actions={
                  <Button variant="normal" href="https://ai.realtimefeedback.com/scorecard">
                    View Scorecards
                  </Button>
                }
              >
                Create a Reputation Scorecard
              </Header>
            }
          >
            <SpaceBetween direction="vertical" size="s">
              <MultiAutoCompleteSearch
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
                loading={createMutation.isPending}
              />
              <Grid gridDefinition={[{ colspan: { default: 12, xs: 6 } }]}>
                <FormField label="Business Type">
                  <Select
                    selectedOption={businessType}
                    options={BUSINESS_TYPES}
                    onChange={({ detail }) => setBusinessType(detail.selectedOption)}
                    disabled={createMutation.isPending}
                    placeholder="Select a business type"
                  />
                </FormField>
              </Grid>
              <ExpandableSection headerText="Add contact information">
                <Grid
                  gridDefinition={[
                    { colspan: { default: 12, xs: 6 } },
                    { colspan: { default: 12, xs: 6 } },
                    { colspan: { default: 12, xs: 6 } },
                    { colspan: { default: 12, xs: 6 } },
                    { colspan: { default: 12, xs: 6 } },
                  ]}
                >
                  <FormField
                    label="First Name"
                    errorText={
                      showErrors && anyContact && !firstName.trim()
                        ? 'First name is required'
                        : undefined
                    }
                  >
                    <Input
                      value={firstName}
                      onChange={({ detail }) => setFirstName(detail.value)}
                      disabled={createMutation.isPending}
                    />
                  </FormField>
                  <FormField label="Last Name">
                    <Input
                      value={lastName}
                      onChange={({ detail }) => setLastName(detail.value)}
                      placeholder="(optional)"
                      disabled={createMutation.isPending}
                    />
                  </FormField>
                  <FormField
                    label="Email"
                    errorText={
                      showErrors && anyContact && !hasContactInfo
                        ? 'Email or phone number is required'
                        : undefined
                    }
                  >
                    <Input
                      value={email}
                      onChange={({ detail }) => setEmail(detail.value)}
                      disabled={createMutation.isPending}
                    />
                  </FormField>
                  <FormField
                    label="Phone Number"
                    errorText={
                      showErrors && anyContact && !hasContactInfo
                        ? 'Email or phone number is required'
                        : undefined
                    }
                  >
                    <Input
                      value={phoneNumber}
                      onChange={({ detail }) => setPhoneNumber(detail.value)}
                      disabled={createMutation.isPending}
                    />
                  </FormField>
                  <FormField label="Title">
                    <Input
                      value={title}
                      onChange={({ detail }) => setTitle(detail.value)}
                      placeholder="(optional)"
                      disabled={createMutation.isPending}
                    />
                  </FormField>
                </Grid>
              </ExpandableSection>
              <Box float="right">
                <Button
                  variant="primary"
                  iconName="add-plus"
                  onClick={handleCreate}
                  loading={createMutation.isPending}
                  disabled={selectedItems.length === 0}
                >
                  {selectedItems.length > 1
                    ? `Create Scorecards (${selectedItems.length})`
                    : 'Create Scorecard'}
                </Button>
              </Box>
              {createMutation.isError && (
                <LoadingOrError loading={createMutation.isPending} error={createMutation.error} />
              )}
            </SpaceBetween>
          </Container>
          {reports.length > 0 && (
            <div>
              {reports.map((report) => (
                <ReportProgress key={report.uuid} uuid={report.uuid} />
              ))}
            </div>
          )}
        </Grid>
      </ViewLayout>
    </>
  );
}

export type ReportProgressProps = {
  uuid: string;
  delivery?: 'text-email' | 'text' | 'email';
};

export function ReportProgress({ uuid, delivery }: ReportProgressProps) {
  const getQuery = useQuery({
    queryKey: ['reports', uuid],
    queryFn: () => getReport(uuid ?? ''),
    enabled: !!uuid,
    // 5 minutes
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });

  return getQuery.isLoading || !getQuery.data || getQuery.error ? (
    <Container variant="stacked">
      <LoadingOrError
        loading={getQuery.isLoading}
        retry={() => getQuery.refetch()}
        error={getQuery.error}
      />
    </Container>
  ) : (
    <ReportStatus report={getQuery.data} delivery={delivery} />
  );
}

function sortTasks(a: Task, b: Task) {
  const aIndex = GOOGLE_PIPELINE_TASKS.indexOf(a.name);
  const bIndex = GOOGLE_PIPELINE_TASKS.indexOf(b.name);
  if (aIndex !== bIndex) {
    return aIndex - bIndex;
  }
  // if the index is the same, sort by min progress
  const aProgress = a.progress ?? 0;
  const bProgress = b.progress ?? 0;
  if (aProgress !== bProgress) {
    return aProgress - bProgress;
  }
  // if the progress is the same, sort by created_at
  return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
}
export type ReportStatusProps = {
  report: PlaceReport;
  delivery?: 'text-email' | 'text' | 'email';
};

export function ReportStatus(props: ReportStatusProps) {
  const [report, setReport] = useState(props.report);
  const { mutateAsync: refreshReport } = useMutation({
    mutationKey: ['reports', 'background-poll'],
    meta: { ignoreNetworkErrors: true },
    mutationFn: () => getReport(props.report.uuid),
    onSuccess: (data) => {
      // simple comparison to see if we need to update the session
      // TODO better comparison
      if (data && JSON.stringify(report) !== JSON.stringify(data)) {
        setReport(data);
      }
    },
  });

  const currentTask = useMemo(() => {
    if (report.tasks.length === 0) {
      return undefined;
    }
    // Only consider Google pipeline tasks for progress tracking
    // This excludes Yelp/BBB/DealerRater tasks that run in parallel
    const filteredTasks = [...report.tasks]
      .filter((task) => GOOGLE_PIPELINE_TASKS.includes(task.name) && task.status !== 'completed')
      .sort(sortTasks);
    return filteredTasks[0];
  }, [report.tasks]);

  // Check if Google source has periods ready - this is the primary source
  // Treat missing/null source as 'google' for backwards compatibility
  const hasGooglePeriods = useMemo(() => {
    return (
      report.periods &&
      report.periods.some((period) => !period.source || period.source === 'google')
    );
  }, [report.periods]);

  const status = useMemo(() => {
    // Show success ONLY when Google periods exist (primary source)
    // This is the definitive check - Google periods are created when
    // report_statistics_source(google) completes successfully
    if (hasGooglePeriods) {
      return 'success';
    }

    // Only show error when ALL tasks are terminal (completed or permanently failed)
    // and no Google periods exist. We can't check individual task failures because
    // collect_reviews_source/report_statistics_source are shared names across all
    // sources (Google, Yelp, BBB, etc.) - a non-Google task failing doesn't mean
    // the Google pipeline has failed.
    const allTasksTerminal =
      report.tasks.length > 0 &&
      report.tasks.every(
        (t) =>
          t.status === 'completed' ||
          (t.status === 'failed' && t.retries != null && t.retries >= 3),
      );
    if (allTasksTerminal) {
      return 'error';
    }

    // Otherwise, still in progress - waiting for Google statistics to complete
    return 'in-progress';
  }, [report.tasks, hasGooglePeriods]);

  const pollReport = useEffectEvent(() => {
    if (document.hidden || navigator.onLine === false) {
      return;
    }

    void refreshReport().catch(() => undefined);
  });

  useEffect(() => {
    if (status !== 'in-progress') {
      return;
    }

    // Poll more frequently (2s) to get progress updates during review collection
    const pollInterval = setInterval(pollReport, 2000);

    return () => clearInterval(pollInterval);
  }, [status]);

  const deliveryMessage =
    props.delivery === 'text-email'
      ? 'Sent by text and email.'
      : props.delivery === 'text'
        ? 'Sent by text.'
        : props.delivery === 'email'
          ? 'Sent by email.'
          : undefined;

  return (
    <SpaceBetween direction="vertical" size="xs">
      <ScorecardLoadingView
        report={report as ScorecardLoadingReport}
        currentTask={currentTask}
        status={status}
        compact
        actionButton={
          <Button
            variant="primary"
            disabled={status !== 'success'}
            href={`/${report.slug ?? report.uuid}`}
            target="_blank"
          >
            View
          </Button>
        }
      />
      {status === 'success' && deliveryMessage && (
        <Box color="text-status-success">{deliveryMessage}</Box>
      )}
    </SpaceBetween>
  );
}
